// FormBuilderPage — the form builder shell: the top header bar (back · form
// icon + title + status/version meta · Form settings / Preview / Save /
// Publish / more) over a two-column body — the Field-library sidebar
// (FormBuilderLibrary) beside the canvas (FormBuilderCanvas). The page owns the
// builder model (pages of fields, local state), seeded from the form detail's
// saved design (seedPagesFromDesign) when one exists; Save flattens it via
// buildElementsPayload → PATCH /forms/update-elements, echoing the server's
// _id/elementGlobalId on seeded elements. Save invalidates the detail, and the
// refetch re-seeds once (seededRef) so fresh ids reach local state — other
// detail refetches (e.g. the settings drawer saving) must NOT re-seed or
// they'd clobber unsaved canvas edits. Publish (PATCH /forms/publish/:id) is
// enabled only while the saved version is a draft, and blocked while there are
// unsaved changes. The kebab holds form testing (PATCH /forms/set-test-status,
// state read back from the detail's `testing`) plus the two exports: Export
// JSON is a pure client-side download of the SAVED design (formExportJson.ts;
// disabled while unsaved changes exist, legacy parity), Export CSV queues an
// export-manager job (POST export-manager/form-design-export, the file arrives
// via the notifications bell). Preview opens FormPreviewOverlay — a phone-frame
// render of the LIVE canvas state, unsaved edits included.
// The body is gated on the detail: spinner while it first loads,
// error + Retry if that load fails, canvas only once `detail` exists — and every
// header action except Back stays disabled until then. Save/Publish gating is
// the load-guard fix proper: `pages` starts as one blank page and
// update-elements REPLACES the element list. `formId` / `formTitle`
// arrive via nav state (create or a row click); the form detail drives the real
// title + status + version meta.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Chip,
  ConfirmModal,
  Dropdown,
  Icon,
  IconButton,
  type ChipVariant,
  type MentionOption,
} from '@/components/common';
import { DEV_ONLY } from '@/config/config';
import useNavigationGuard from '@/hooks/useNavigationGuard';
import { downloadTextFile } from '@/utils/downloadUrl';
import useModuleAccess, {
  useCanExportFormTemplates,
  useCanPublishFormTemplate,
} from '@/hooks/useModuleAccess';
import {
  FormVersionState,
  useExportFormDesignMutation,
  useGetFormDetailQuery,
  usePublishFormMutation,
  useUpdateFormElementsMutation,
  type FormStatus,
} from '@/api/endpoints/formsApi';
import { useFormMediaMutation } from '@/api/endpoints/mediaApi';
import { useGetDataSourceSelectionQuery } from '@/api/endpoints/datasourceApi';
import {
  buildElementsPayload,
  createBlankPage,
  createDefaultPages,
  createLibraryField,
  seedPagesFromDesign,
  uploadPendingFieldImages,
} from './utils/builderPayload';
import {
  type ChoiceOption,
  type DataSourceBinding,
  type ElementProperties,
  type LibraryElementType,
  type PageProperties,
} from './utils/formElementDefaults';
import { FIELD_LIBRARY } from './utils/fieldLibrary';
import {
  fieldBlockRange,
  moveBlockTo,
  moveTopLevelItem,
  sectionBlockRange,
  swapMemberBlocks,
} from './utils/canvasItems';
import { buildFormExportJson, formExportFileName } from './utils/formExportJson';
import {
  cascadeOptionChange,
  copyRowsRemapped,
  deleteFieldBlock,
  deleteRuleCascade,
  duplicateFieldBlock,
  insertRuleChild,
  newRule,
  removedOptionValues,
  stripGotoRulesToPage,
  type RuleActionSpec,
} from './utils/triggerOps';
import { removeBindingsToFields } from './utils/bindingOps';
import FormLogicDrawer from './FormLogicDrawer';
import {
  countCompanyStructure,
  describeIssues,
  isCompanyStructure,
  validateBuilderPages,
} from './utils/builderValidation';
import FormTestingModal from './FormTestingModal';
import FormPreviewOverlay from './FormPreviewOverlay';
import FormSettingsDrawer from './settings/FormSettingsDrawer';
import FormPagesDrawer from './FormPagesDrawer';
import FormBuilderLibrary from './FormBuilderLibrary';
import type { BuilderField, BuilderPage, BuilderRuleAction, GeofenceGate } from './utils/builderModel';
import FormBuilderCanvas from './FormBuilderCanvas';
import FormBuilderPropertiesPanel from './FormBuilderPropertiesPanel';

// Field types a dataSource binding may target (kept narrow for now).
// `location` is here for geofence: binding a geoCoordinates column to a
// location field is what enables its geofence toggle (the legacy app offered
// every element type as a binding target).
const BINDABLE_TYPES: LibraryElementType[] = ['text', 'number', 'checkList', 'location'];

// Status chip tone for the builder header.
const STATUS_VARIANT: Record<FormStatus, ChipVariant> = {
  draft: 'neutral',
  published: 'success',
  suspended: 'warning',
};

const FormBuilderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, formTitle } =
    (location.state as { formId?: string; formTitle?: string } | null) ?? {};

  // The builder is a pure EDIT surface (creation happens in CreateFormModal
  // before arriving here), so it requires the `edit` verb — see the redirect
  // below. Publish and the export stubs carry their own extras on top.
  const { canEdit } = useModuleAccess('forms_templates');
  const canPublishForm = useCanPublishFormTemplate();
  const canExportForms = useCanExportFormTemplates();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  // Builder model — pages of elements in local state, seeded from the saved
  // design when one exists. Every element is { elementType, properties,
  // collapsed } per the app model, plus a client-only id; properties start as
  // the type's defaults.
  const [pages, setPages] = useState<BuilderPage[]>(() => [createBlankPage()]);
  // Any design mutation flips this; only a successful save (or a re-seed, which
  // IS the saved design) clears it. Deliberately a "touched" marker and NOT a
  // diff against a baseline: editing a title and typing the original value back
  // still leaves the form changed. Collapse state is the one exclusion — it never
  // reaches the wire (see updateFieldsUiOnly).
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activePageId, setActivePageId] = useState(pages[0].id);
  // What the properties panel describes: the active page, a field on it, or
  // nothing (panel closed). Field selection also drives the card highlight.
  const [selection, setSelection] = useState<
    { kind: 'page' } | { kind: 'field'; id: string } | null
  >(null);
  const selectedFieldId = selection?.kind === 'field' ? selection.id : null;

  // Switching pages selects the page (opens its properties panel).
  const selectPage = (id: string) => {
    setActivePageId(id);
    setSelection({ kind: 'page' });
  };
  const selectField = (id: string) => setSelection({ kind: 'field', id });

  // Apply an update to the active page's field list WITHOUT marking the design
  // dirty — for state that never reaches the wire (collapse; see below).
  const updateFieldsUiOnly = (fn: (fields: BuilderField[]) => BuilderField[]) =>
    setPages((prev) =>
      prev.map((p) => (p.id === activePageId ? { ...p, fields: fn(p.fields) } : p)),
    );

  // The funnel for every real field mutation — same write, plus the dirty flag.
  const updateFields = (fn: (fields: BuilderField[]) => BuilderField[]) => {
    setHasUnsavedChanges(true);
    updateFieldsUiOnly(fn);
  };

  // Add a field to the active page — at `index` (a canvas drop's gap) when
  // given, appended otherwise (the library's double-click path, which lands
  // top-level: appending never joins a section). `sectionId` names the section
  // a body drop targets; sections themselves are always top-level.
  // companyStructure is exactly-once per form: the library hides it while one
  // exists, and this refuses a drag that started before it hid.
  const hasCompanyStructure = countCompanyStructure(pages) > 0;
  const addField = (type: LibraryElementType, index?: number, sectionId: string | null = null) => {
    if (type === 'companyStructure' && hasCompanyStructure) return;
    updateFields((fields) => {
      const field = createLibraryField(type, sectionId);
      const at = index === undefined ? fields.length : Math.min(Math.max(index, 0), fields.length);
      return [...fields.slice(0, at), field, ...fields.slice(at)];
    });
  };
  const addPage = () => {
    const page = createBlankPage();
    setHasUnsavedChanges(true);
    setPages((prev) => [...prev, page]);
    setActivePageId(page.id);
    setSelection({ kind: 'page' });
  };
  const movePage = (pageId: string, dir: -1 | 1) => {
    setHasUnsavedChanges(true);
    setPages((prev) => {
      const i = prev.findIndex((p) => p.id === pageId);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const deletePage = (pageId: string) => {
    const i = pages.findIndex((p) => p.id === pageId);
    if (pages.length <= 1 || i < 0) return; // the last page can't be deleted
    // Go-to rules across every other page that jumped to this one die with it,
    // and bindings on other pages may point at the fields it held.
    const next = removeBindingsToFields(
      stripGotoRulesToPage(
        pages.filter((p) => p.id !== pageId),
        pageId,
      ),
      pages[i].fields,
    );
    setHasUnsavedChanges(true);
    setPages(next);
    // Deleting the active page activates its previous neighbor (or the new
    // first page).
    if (activePageId === pageId) setActivePageId(next[Math.max(0, i - 1)].id);
    setSelection(null);
  };
  // Rename any page by id (updatePageProperties is active-page-only).
  const renamePage = (pageId: string, title: string) => {
    setHasUnsavedChanges(true);
    setPages((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? {
              ...p,
              properties: {
                ...p.properties,
                basicProperties: { ...p.properties.basicProperties, pageTitle: title },
              },
            }
          : p,
      ),
    );
  };
  // Duplicate a page deep — fresh page identity, every field copied with fresh
  // ids and its sectionId/trigger references re-pointed at the copies
  // (copyRowsRemapped). user_page rules on the copy still target the pages the
  // SOURCE targeted, and rules elsewhere keep targeting the source page, never
  // the copy (legacy semantics). The companyStructure field stays behind —
  // exactly one per form; it is never a follow-up or binding target, so
  // leaving it out orphans no reference.
  const duplicatePage = (pageId: string) => {
    setHasUnsavedChanges(true);
    setPages((prev) => {
      const i = prev.findIndex((p) => p.id === pageId);
      if (i < 0) return prev;
      const source = prev[i];
      const copy: BuilderPage = {
        id: uuidv4(),
        _id: null,
        elementGlobalId: null,
        elementType: 'page',
        properties: structuredClone(source.properties),
        fields: copyRowsRemapped(source.fields.filter((f) => !isCompanyStructure(f))),
        triggerFlowReferenceId: uuidv4(),
      };
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });
  };
  // Collapse is presentation only — not sent, and reset to false on every load —
  // so collapsing a card must not make the form "unsaved".
  const collapseAll = (collapsed: boolean) =>
    updateFieldsUiOnly((fields) => fields.map((f) => ({ ...f, collapsed })));
  const toggleCollapse = (fieldId: string) =>
    updateFieldsUiOnly((fields) =>
      fields.map((f) => (f.id === fieldId ? { ...f, collapsed: !f.collapsed } : f)),
    );
  // Move a field WITH its follow-up block: a section member swaps with the
  // neighboring member block (bounded inside its section — confirmed); a
  // top-level field swaps with its neighbor ITEM, jumping over a whole section
  // block in one step. Follow-up rows themselves are not movable (no UI).
  const moveField = (fieldId: string, dir: -1 | 1) =>
    updateFields((fields) => {
      const field = fields.find((f) => f.id === fieldId);
      if (!field || field.triggerParentId !== null) return fields;
      if (field.sectionId === null) return moveTopLevelItem(fields, fieldId, dir);
      return swapMemberBlocks(fields, fieldId, dir);
    });
  // Move a whole section block (row + children) among the top-level items —
  // its neighbor may be another block or a top-level field.
  const moveSection = (sectionId: string, dir: -1 | 1) =>
    updateFields((fields) => moveTopLevelItem(fields, sectionId, dir));
  // A reorder drag's drop: move a row's whole block to a gap's flat index,
  // joining `sectionId` (null = top-level). Computed BEFORE dispatching —
  // updateFields marks the design dirty unconditionally, and an unchanged
  // result (no-op drop, guard-rejected index, or a MOVE payload from another
  // tab whose id doesn't resolve here) must not block Publish or arm the
  // leave guard. Drops run in the current render, so these fields are fresh.
  const moveFieldTo = (id: string, index: number, sectionId: string | null = null) => {
    const page = pages.find((p) => p.id === activePageId) ?? pages[0];
    if (!page) return;
    const next = moveBlockTo(page.fields, id, index, sectionId);
    if (next === page.fields) return;
    updateFields(() => next);
  };
  // Duplicate a field WITH its follow-up block — copies get fresh identities
  // and their internal rule/parent references re-pointed (triggerOps).
  const duplicateField = (fieldId: string) =>
    updateFields((fields) => duplicateFieldBlock(fields, fieldId));
  // Duplicate a section deep — the row AND its fields (incl. their follow-up
  // blocks), every copy with fresh ids (confirmed) — inserted right after the
  // original block. copyRowsRemapped re-points sectionId/trigger references at
  // the copies. A member companyStructure stays behind (exactly one per form).
  const duplicateSection = (sectionId: string) =>
    updateFields((fields) => {
      const range = sectionBlockRange(fields, sectionId);
      if (!range) return fields;
      const copies = copyRowsRemapped(
        fields.slice(range[0], range[1]).filter((f) => !isCompanyStructure(f)),
      );
      return [...fields.slice(0, range[1]), ...copies, ...fields.slice(range[1])];
    });
  // Delete a section WITH its fields (confirmed) — the canvas confirms first.
  // Bindings on any page may point at the removed members.
  const deleteSection = (sectionId: string) => {
    const fields = pages.find((p) => p.id === activePageId)?.fields ?? [];
    const range = sectionBlockRange(fields, sectionId);
    const removed = range ? fields.slice(range[0], range[1]) : [];
    updateFields((fs) => {
      const r = sectionBlockRange(fs, sectionId);
      if (!r) return fs;
      return [...fs.slice(0, r[0]), ...fs.slice(r[1])];
    });
    setPages((prev) => removeBindingsToFields(prev, removed));
    setSelection(null);
  };
  // Delete a field WITH its follow-up block (the canvas confirms first when
  // follow-ups exist). The selection may sit on a row inside the block, and
  // bindings on any page may point at the removed rows.
  const deleteField = (fieldId: string) => {
    const fields = pages.find((p) => p.id === activePageId)?.fields ?? [];
    const range = fieldBlockRange(fields, fieldId);
    const removed = range ? fields.slice(range[0], range[1]) : [];
    const doomed = new Set(range ? removed.map((f) => f.id) : [fieldId]);
    updateFields((fs) => deleteFieldBlock(fs, fieldId));
    setPages((prev) => removeBindingsToFields(prev, removed));
    setSelection((sel) => (sel?.kind === 'field' && doomed.has(sel.id) ? null : sel));
  };
  // Replace an element's properties. checkList optionList writes are diffed
  // first: an option deleted or its VALUE edited kills the rules matching the
  // old value, cascading their follow-ups — silently, legacy-exact. (Applying
  // a response set replaces values wholesale and must kill ALL rules even when
  // values overlap — that goes through applyResponseSet, not this diff.)
  const updateFieldProperties = (fieldId: string, properties: ElementProperties) =>
    updateFields((fields) => {
      const prev = fields.find((f) => f.id === fieldId);
      let next = fields.map((f) => (f.id === fieldId ? { ...f, properties } : f));
      if (prev?.elementType === 'checkList' && prev.rules.length) {
        const optionsOf = (elementProps: ElementProperties) =>
          ((elementProps.basicProperties as Record<string, unknown>)
            .optionList as ChoiceOption[]) ?? [];
        const removed = removedOptionValues(optionsOf(prev.properties), optionsOf(properties));
        if (removed.length) next = cascadeOptionChange(next, fieldId, removed);
      }
      return next;
    });
  // Replace the active page's own properties (page panel edits).
  const updatePageProperties = (properties: ElementProperties) => {
    setHasUnsavedChanges(true);
    setPages((prev) =>
      prev.map((p) =>
        p.id === activePageId ? { ...p, properties: properties as PageProperties } : p,
      ),
    );
  };

  // ---- Conditional logic (IF rules on checkList fields) ----
  // The drawer edits LIVE — every handler goes through the dirty funnel.
  const [logicFieldId, setLogicFieldId] = useState<string | null>(null);

  const addRule = (fieldId: string) =>
    updateFields((fields) =>
      fields.map((f) => (f.id === fieldId ? { ...f, rules: [...f.rules, newRule()] } : f)),
    );
  const setRuleAnswer = (fieldId: string, ruleId: string, value: string) =>
    updateFields((fields) =>
      fields.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              rules: f.rules.map((r) => (r.id === ruleId ? { ...r, matchingValue: value } : r)),
            }
          : f,
      ),
    );
  // Set/replace a rule's action. Leaving an ask action (or changing its answer
  // type) cascade-deletes the old follow-up block; picking ask creates and
  // places the new follow-up (insertRuleChild). One state write for all of it.
  const setRuleAction = (fieldId: string, ruleId: string, spec: RuleActionSpec) =>
    updateFields((fields) => {
      const parent = fields.find((f) => f.id === fieldId);
      const rule = parent?.rules.find((r) => r.id === ruleId);
      if (!parent || !rule) return fields;
      let next = fields;
      if (rule.action?.kind === 'ask_a_question') {
        const child = fields.find((f) => f.id === (rule.action as { childId: string }).childId);
        if (spec.kind === 'ask_a_question' && child?.elementType === spec.childType) return fields;
        next = deleteFieldBlock(next, (rule.action as { childId: string }).childId);
      } else if (rule.action?.kind === spec.kind && spec.kind !== 'user_page') {
        return fields; // the active bare tile re-clicked — nothing to change
      }
      let action: BuilderRuleAction;
      if (spec.kind === 'ask_a_question') {
        const inserted = insertRuleChild(next, fieldId, ruleId, spec.childType);
        next = inserted.fields;
        action = { kind: 'ask_a_question', childId: inserted.childId };
      } else if (spec.kind === 'user_page') {
        action = { kind: 'user_page', pageId: spec.pageId };
      } else {
        action = { kind: spec.kind };
      }
      return next.map((f) =>
        f.id === fieldId
          ? { ...f, rules: f.rules.map((r) => (r.id === ruleId ? { ...r, action } : r)) }
          : f,
      );
    });
  const deleteRule = (fieldId: string, ruleId: string) =>
    updateFields((fields) => deleteRuleCascade(fields, fieldId, ruleId));
  // A response set replaces the optionList wholesale — every rule dies with the
  // old options (values may overlap, so this can't ride the optionList diff).
  const applyResponseSet = (fieldId: string, options: ChoiceOption[]) =>
    updateFields((fields) =>
      cascadeOptionChange(fields, fieldId, 'all').map((f) =>
        f.id === fieldId
          ? ({
              ...f,
              properties: {
                ...f.properties,
                basicProperties: {
                  ...(f.properties.basicProperties as object),
                  optionList: options,
                },
              } as ElementProperties,
            } as BuilderField)
          : f,
      ),
    );

  // No form id in the nav state → nothing to build; go back to the list. Safe
  // past the guard below: with no formId nothing has been edited, so it's off.
  useEffect(() => {
    if (!formId) navigate('/form-templates');
  }, [formId, navigate]);

  // In-app navigation guard. The Back button needs no special case — it calls
  // navigate('/form-templates'), which the guard already intercepts.
  // useState holding a function: the setter treats a bare function as an
  // updater, hence the extra arrow.
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  useNavigationGuard(hasUnsavedChanges, (proceed) => setPendingNav(() => proceed));

  // Reload / tab close. Browsers show their own fixed wording and ignore any
  // custom message; preventDefault + returnValue is only the "please ask" signal.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);

  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    refetch: refetchDetail,
  } = useGetFormDetailQuery(formId ?? '', { skip: !formId });
  const [updateElements, { isLoading: saving }] = useUpdateFormElementsMutation();
  const [publishForm, { isLoading: publishing }] = usePublishFormMutation();
  const [exportFormDesign, { isLoading: exportingCsv }] = useExportFormDesignMutation();
  const [formMedia] = useFormMediaMutation();
  // Sources catalog (columns carry dataType) — needed only while a location
  // field is selected, to gate its geofence toggle on a geoCoordinates binding.
  const locationSelected =
    selection?.kind === 'field' &&
    pages.some((p) => p.fields.some((f) => f.id === selection.id && f.elementType === 'location'));
  const { data: dataSources = [] } = useGetDataSourceSelectionQuery(undefined, {
    skip: !locationSelected,
  });
  // Covers the save's upload phase, which runs before `saving` flips — so the
  // button spins (and blocks a second click) for the whole operation.
  const [uploading, setUploading] = useState(false);
  const title = detail?.name || formTitle || t('forms_builder_title');
  // Header chip = the current design version's state (a never-saved form reads
  // as Draft; the form-level suspended status isn't surfaced here).
  const designStatus = detail?.design?.versionState ?? FormVersionState.Draft;
  // Publish only makes sense while the saved current version is a draft.
  const canPublish = detail?.design?.versionState === FormVersionState.Draft;

  // Load gates. `pages` starts as one blank page, and update-elements REPLACES
  // the element list — so saving before the saved design has arrived would
  // destroy it. Hence Save/Publish stay disabled until `detail` exists.
  // `isLoading`, not `isFetching`: the post-save invalidation refetch must not
  // blank the canvas or disable Save. `isError && !detail`: RTK Query keeps the
  // last good data when a *refetch* fails, and an already-seeded canvas (with
  // possibly unsaved edits) must not be hidden behind an error screen — that
  // design is real, so the canvas and Save stay live.
  const detailReady = Boolean(detail);
  const loadFailed = detailError && !detail;

  // Seed the builder from the detail's saved design — once on first arrival,
  // and once more after each save (the save invalidates the detail, and the
  // refetched design carries the fresh _id/elementGlobalId the next save must
  // echo). Guarded by seededRef: other detail refetches (e.g. the settings
  // drawer saving the title) must not re-seed over unsaved canvas edits.
  // A never-saved design starts from the default canvas instead (a page holding
  // the form's required companyStructure field). Only reachable on first arrival:
  // Save validation requires that field, so a post-save refetch is never empty.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !detail) return;
    seededRef.current = true;
    const seeded = seedPagesFromDesign(detail.design?.elements ?? []);
    const fresh = !seeded.length;
    const next = fresh ? createDefaultPages() : seeded;
    // A re-seed IS the saved design, so it clears the flag — this is what settles
    // it after the post-save refetch lands. The default canvas isn't on the
    // server yet, so it starts dirty.
    setHasUnsavedChanges(fresh);
    setPages(next);
    setActivePageId(next[0].id);
    setSelection(null);
  }, [detail]);

  // Test mode comes from the DETAIL, so the menu label survives a reload.
  const testing = detail?.testing === true;

  // Kebab menu row — the FormRowActions house shape (icon + label, closes first).
  // `disabled` keeps the row visible with a title hint (the Publish idiom).
  const menuItem = (
    label: string,
    icon: string,
    onClick: () => void,
    opts?: { disabled?: boolean; title?: string },
  ) => (
    <button
      type='button'
      className='app-dropdown__item'
      role='menuitem'
      disabled={opts?.disabled}
      title={opts?.disabled ? opts?.title : undefined}
      onClick={() => {
        setMenuOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  // Export JSON — pure client-side download of the SAVED design (no export
  // route exists; the legacy app did the same). The menu item is disabled
  // while there are unsaved changes so the file always matches the server
  // state; the post-save detail refetch keeps `detail.design` fresh.
  const handleExportJson = () => {
    if (!detail) return;
    try {
      downloadTextFile(
        buildFormExportJson(detail),
        formExportFileName(detail.name),
        'application/json;charset=utf-8',
      );
      toast.success(t('forms_builder_export_json_success'));
    } catch {
      toast.error(t('forms_builder_export_json_failed'));
    }
  };

  // Save — flatten the pages state into the update-elements payload, then let
  // the invalidation-triggered detail refetch re-seed the fresh server ids.
  const handleSave = async () => {
    if (!formId) return;
    // Never PATCH a canvas we can't trust: no saved design loaded yet, or an
    // empty page list (structurally impossible — deletePage keeps the last page
    // — so a refactor that breaks that guard fails loudly here, not silently
    // server-side).
    if (!detail || !pages.length) {
      toast.error(t('error_generic'));
      return;
    }
    // Validate BEFORE the upload below: uploadPendingFieldImages commits files to
    // storage and writes their descriptors back into state, so validating after it
    // would leave irreversible side effects behind a save that never happened.
    const issues = validateBuilderPages(pages);
    if (issues.length) {
      toast.error(describeIssues(issues, t));
      return;
    }
    try {
      setUploading(true);
      // Pending image-field files are uploaded HERE and nowhere else — picking a
      // file only stashes it in state, so saving is what commits it. A failed
      // upload throws, so the PATCH never runs and nothing is half-saved.
      const prepared = await uploadPendingFieldImages(pages, {
        formId,
        versionId: detail.design.id,
        reserve: formMedia,
      });
      // Commit the resolved descriptors before the PATCH, so a PATCH failure (or
      // any later save) re-sends but never re-uploads the same file.
      if (prepared !== pages) setPages(prepared);
      await updateElements({ formId, elements: buildElementsPayload(prepared) }).unwrap();
      seededRef.current = false;
      // Only after the PATCH resolves: a validation bail or a failed request has
      // to leave the design dirty.
      setHasUnsavedChanges(false);
      toast.success(t('forms_builder_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setUploading(false);
    }
  };

  // Publish the saved draft version. The design elements don't change, so no
  // re-seed — the invalidated detail refetch updates status/versionState.
  const handlePublish = async () => {
    if (!formId) return;
    try {
      await publishForm(formId).unwrap();
      toast.success(t('forms_builder_published'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Export CSV queues a server-side job over the saved design — nothing comes
  // back to download, so success just confirms the kick-off; the file arrives
  // through the notifications bell. Menu items carry no loading affordance, so
  // re-entry is guarded here.
  const handleExportCsv = async () => {
    if (!formId || exportingCsv) return;
    try {
      await exportFormDesign(formId).unwrap();
      toast.success(t('forms_builder_export_started'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Fields a dataSource binding can point at — the whole form, not just the
  // active page. Identified by `elementGlobalId ?? id`: the client uuid stands
  // in until the first save, and buildElementsPayload sends it as
  // `uniqueIdToReplace` so the reference survives.
  const bindableFields = pages.flatMap((page) =>
    page.fields
      .filter((f) => BINDABLE_TYPES.includes(f.elementType))
      .flatMap((f) => {
        // The wire id, never the client-only React key: saved elements bind by
        // elementGlobalId, never-saved ones by their uniqueIdToReplace.
        const id = f.elementGlobalId ?? f.uniqueIdToReplace;
        if (!id) return [];
        const bp = f.properties.basicProperties as Record<string, unknown>;
        const entry = FIELD_LIBRARY.find((e) => e.type === f.elementType);
        return [
          {
            id,
            title: (bp.fieldTitle as string) || (entry ? t(entry.labelKey) : f.elementType),
          },
        ];
      }),
  );

  // Watermark mention pool — the legacy filter, WIDER than BINDABLE_TYPES:
  // every field except section/image/dataSource (legacy also excluded
  // externalApi, which this builder doesn't offer). `display` doubles as the
  // watermarkUsedFieldsMap key and is embedded in watermarkText, so it must be
  // stable DATA: untitled fields are skipped (they can't save anyway —
  // titleRequired) instead of ported legacy id-as-display + save-time rewrite;
  // duplicate displays are skipped too (first wins), since a display can only
  // map to one value.
  const watermarkMentionFields: MentionOption[] = (() => {
    const excluded = new Set(['section', 'image', 'dataSource']);
    const seen = new Set(['User Name', 'Date And Time']);
    const out: MentionOption[] = [];
    for (const page of pages) {
      for (const f of page.fields) {
        if (excluded.has(f.elementType)) continue;
        const id = f.elementGlobalId ?? f.uniqueIdToReplace;
        if (!id) continue;
        const bp = f.properties.basicProperties as Record<string, unknown>;
        const title = ((bp.fieldTitle as string) ?? '').trim();
        if (!title || seen.has(title)) continue;
        seen.add(title);
        const entry = FIELD_LIBRARY.find((e) => e.type === f.elementType);
        out.push({ value: id, display: title, icon: entry?.icon });
      }
    }
    return out;
  })();

  const activePage = pages.find((p) => p.id === activePageId) ?? null;
  const selectedField =
    selection?.kind === 'field'
      ? (activePage?.fields.find((f) => f.id === selection.id) ?? null)
      : null;
  // Resolved live so the drawer always renders the current rules.
  const logicField = logicFieldId
    ? (activePage?.fields.find((f) => f.id === logicFieldId) ?? null)
    : null;
  // What the properties panel edits: a selected field, or the active page.
  const panelTarget = selectedField ?? (selection?.kind === 'page' ? activePage : null);

  // Geofence gating for the selected location field — the legacy contract
  // (FormProperties.checkGeofenceToggleEnabled in the pre-rewrite app): the
  // switch may only turn ON while this field is bound to a geoCoordinates
  // data-source column, and only one location field per form may hold geofence.
  const geofenceGate: GeofenceGate | undefined = (() => {
    if (!selectedField || selectedField.elementType !== 'location') return undefined;
    // The wire id, same rule as bindableFields: bindings never use the React key.
    const wireId = selectedField.elementGlobalId ?? selectedField.uniqueIdToReplace;
    const allFields = pages.flatMap((p) => p.fields);
    const bound =
      !!wireId &&
      allFields.some((f) => {
        if (f.elementType !== 'dataSource') return false;
        const bp = f.properties.basicProperties as Record<string, unknown>;
        const entry = (bp.bindingList as DataSourceBinding[] | undefined)?.find(
          (b) => b.fieldId === wireId,
        );
        if (!entry) return false;
        const source = dataSources.find((s) => s.id === (bp.dataSource as string));
        return !!source?.columns.some(
          (c) => c.columnId === entry.columnId && c.dataType === 'geoCoordinates',
        );
      });
    // Legacy order: one-per-form only matters once the binding allows enabling.
    const takenByOther =
      bound &&
      allFields.some(
        (f) =>
          f.elementType === 'location' &&
          f.id !== selectedField.id &&
          (f.properties as { validationProperties?: Record<string, unknown> }).validationProperties
            ?.geofence === true,
      );
    return { bound, takenByOther };
  })();

  // No form id → the redirect effect is firing; render nothing meanwhile (also
  // narrows formId to string for the settings drawer).
  // Without `edit` the canvas must not render at all — the module has no
  // read-only builder mode, and the route is reachable by URL/forged state
  // for anyone with module view (the RoleEditorPage idiom).
  if (!canEdit) return <Navigate to='/form-templates' replace />;

  if (!formId) return null;

  return (
    <div className='form-builder'>
      <header className='form-builder__bar'>
        <div className='form-builder__lead'>
          <IconButton
            icon='arrow-left'
            variant='outline'
            aria-label={t('forms_builder_back')}
            onClick={() => navigate('/form-templates')}
          />
          <span className='form-builder__tile' aria-hidden='true'>
            <Icon name='calendar-check' size={20} />
          </span>
          <div className='form-builder__heading'>
            <h1 className='form-builder__title'>{title}</h1>
            <div className='form-builder__meta'>
              <Chip variant={STATUS_VARIANT[designStatus]} dot>
                {t(`forms_status_${designStatus}`)}
              </Chip>
              {/* Version number only — a bare `vN` marker, not translated copy,
               * so no i18n key. */}
              {detail && <span className='form-builder__version'>v{detail.design.version}</span>}
            </div>
          </div>
        </div>

        <div className='form-builder__actions'>
          {/* Every action but Back is gated on the detail — the settings tabs
           * seed from it, and there is nothing to preview or act on until the
           * saved design has arrived. */}
          <Button
            variant='secondary'
            iconStart='sliders-horizontal'
            disabled={!detailReady}
            onClick={() => setSettingsOpen(true)}
          >
            {t('forms_builder_settings')}
          </Button>
          <Button
            variant='secondary'
            iconStart='smartphone'
            disabled={!detailReady}
            onClick={() => setPreviewOpen(true)}
          >
            {t('forms_builder_preview')}
          </Button>
          <Button
            variant='secondary'
            iconStart='save'
            disabled={!detailReady}
            loading={saving || uploading}
            onClick={handleSave}
          >
            {t('forms_builder_save')}
          </Button>
          {/* The `publish` extra gates WHO may publish; the state checks below
              gate WHEN (draft only, saved first). It presupposes `edit` in
              practice — a draft only exists once someone saved one. */}
          {canPublishForm && (
            <Button
              iconStart='rocket'
              // Publish acts on the SAVED version, so publishing with edits on the
              // canvas would ship something other than what's on screen. Save first.
              disabled={!canPublish || !detailReady || hasUnsavedChanges}
              title={hasUnsavedChanges ? t('forms_builder_publish_save_first') : undefined}
              loading={publishing}
              onClick={handlePublish}
            >
              {t('forms_builder_publish')}
            </Button>
          )}
          <Dropdown
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            align='end'
            width={210}
            portal
            trigger={
              <IconButton
                icon='ellipsis'
                variant='outline'
                aria-label={t('forms_builder_more')}
                disabled={!detailReady}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              />
            }
          >
            {/* One item, two labels: testing is read from the detail, so it
                survives a reload. Export JSON downloads the saved design
                client-side; Export CSV queues an export-manager job. */}
            {menuItem(
              t(testing ? 'forms_builder_view_testing' : 'forms_builder_start_testing'),
              'flask-conical',
              () => setTestOpen(true),
            )}
            {canExportForms && (
              <>
                <div className='app-dropdown__divider' role='separator' />
                {menuItem(t('forms_builder_export_json'), 'braces', handleExportJson, {
                  disabled: hasUnsavedChanges,
                  title: t('forms_builder_publish_save_first'),
                })}
                {menuItem(t('forms_builder_export_csv'), 'file-spreadsheet', handleExportCsv)}
              </>
            )}
          </Dropdown>
        </div>
      </header>

      {/* The status block replaces the WHOLE body (library included): a library
       * left clickable while the design loads would let the user add fields
       * that the seed then silently discards. */}
      <div className='form-builder__body'>
        {detailLoading ? (
          <div className='form-builder__status' role='status'>
            <span className='form-builder__spinner' aria-hidden='true' />
            {t('text_loading')}
          </div>
        ) : loadFailed ? (
          <div className='form-builder__status'>
            <Icon name='circle-alert' size={28} />
            <p className='form-builder__status-text'>{t('forms_builder_load_error')}</p>
            <Button variant='secondary' iconStart='rotate-cw' onClick={() => refetchDetail()}>
              {t('forms_builder_retry')}
            </Button>
          </div>
        ) : (
          <>
            <FormBuilderLibrary
              onAdd={addField}
              hiddenTypes={hasCompanyStructure ? ['companyStructure'] : undefined}
            />
            <FormBuilderCanvas
              pages={pages}
              activePageId={activePageId}
              selectedFieldId={selectedFieldId}
              onSelectField={selectField}
              onSelectPage={selectPage}
              onDropField={addField}
              onMoveExisting={moveFieldTo}
              onOpenLogic={setLogicFieldId}
              onManagePages={() => setPagesOpen(true)}
              onAddPage={addPage}
              onMovePage={movePage}
              onDuplicatePage={duplicatePage}
              onDeletePage={deletePage}
              onCollapseAll={collapseAll}
              onToggleCollapse={toggleCollapse}
              onMove={moveField}
              onDuplicate={duplicateField}
              onDelete={deleteField}
              onMoveSection={moveSection}
              onDuplicateSection={duplicateSection}
              onDeleteSection={deleteSection}
            />
            {panelTarget && (
              <FormBuilderPropertiesPanel
                field={panelTarget}
                bindableFields={bindableFields}
                geofenceGate={geofenceGate}
                watermarkMentions={watermarkMentionFields}
                onChange={(props) =>
                  selectedField
                    ? updateFieldProperties(selectedField.id, props)
                    : updatePageProperties(props)
                }
                onApplyResponseSet={
                  selectedField
                    ? (options) => applyResponseSet(selectedField.id, options)
                    : undefined
                }
                onClose={() => setSelection(null)}
              />
            )}
          </>
        )}
      </div>

      <FormPagesDrawer
        open={pagesOpen}
        onClose={() => setPagesOpen(false)}
        pages={pages}
        activePageId={activePageId}
        onOpenPage={setActivePageId}
        onRenamePage={renamePage}
        onMovePage={movePage}
        onDuplicatePage={duplicatePage}
        onDeletePage={deletePage}
        onAddPage={addPage}
      />

      {/* Conditional logic is DEV_ONLY while the feature is unshipped. Both of
       * setLogicFieldId's callers (the card's IF button and the rules banner's
       * "View logic") are gated too — not rendering the drawer at all makes it
       * unreachable by construction rather than by audit. Serialization and the
       * load seed stay ungated on purpose: environments share a backend, so
       * stripping triggers here would destroy rules authored on develop. */}
      {DEV_ONLY && (
        <FormLogicDrawer
          open={Boolean(logicField)}
          onClose={() => setLogicFieldId(null)}
          field={logicField}
          fields={activePage?.fields ?? []}
          pages={pages}
          activePageId={activePageId}
          onAddRule={addRule}
          onSetRuleAnswer={setRuleAnswer}
          onSetRuleAction={setRuleAction}
          onDeleteRule={deleteRule}
          onUpdateFieldProperties={updateFieldProperties}
          onApplyResponseSet={applyResponseSet}
        />
      )}

      <FormSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        formId={formId}
        detail={detail}
      />

      <FormPreviewOverlay
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={title}
        pages={pages}
        activePageId={activePageId}
      />

      {testOpen && (
        <FormTestingModal
          open
          onClose={() => setTestOpen(false)}
          formId={formId}
          testing={testing}
        />
      )}

      {pendingNav && (
        <ConfirmModal
          open
          onClose={() => setPendingNav(null)}
          onConfirm={() => {
            const go = pendingNav;
            setPendingNav(null);
            go();
          }}
          tone='warning'
          icon='triangle-alert'
          title={t('forms_builder_leave_title')}
          description={t('forms_builder_leave_body')}
          confirmLabel={t('forms_builder_leave_confirm')}
          cancelLabel={t('forms_builder_leave_stay')}
          confirmVariant='danger'
        />
      )}
    </div>
  );
};

export default FormBuilderPage;
